<style>
h3 {
  @apply text-lg font-medium text-left;
  @apply mb-8;
}

ul {
  @apply list-disc pl-6 my-4;
}
.addMore {
  @apply flex items-center mt-6 cursor-pointer;
  font-size: 15px;
  color: var(--primary);
}
.addMore:hover {
  @apply opacity-75;
}
.loading {
  @apply cursor-wait;
}
.remove {
  @apply absolute flex items-center text-red-600 cursor-pointer;
  font-size: 15px;
  right: 16px;
}
.hint {
  @apply text-left text-base px-4 mt-4 mb-4;
}
.error {
  @apply my-2 text-center;
  color: #c4272a;
}
</style>

<script>
import Button from "./Button.svelte"
import SubHeading from "./SubHeading.svelte"
import { workflowLang } from "@shared/store/workflowKey"
import {
  removeText,
  addAnotherText,
  loadingText,
  errorUploadingFileText,
  errorRemovingFileText,
  errorUploadingFileWithSameNameText,
} from "@shared/utils/translations"
import API from "@local/utils/api"

let _errors

export let value = undefined
export let label = ""
export let hintTitle = ""
export let hintItems = []
export let description = ""
export let buttonHint = ""
export let multi = false
export let buttonLabel = ""
export let buttonColor = undefined
export let buttonFilled = true
export let buttonOutlined = false
export let role = undefined
export let name = ""
export let wfId = undefined
export let userId = undefined
export let allowedExtension = undefined
export let content = multi
  ? Array.isArray(value)
    ? value
    : []
  : value ?? undefined

export { _errors as errors }

let uploadInput

$: loading = false
$: errors = _errors || []
$: hasErrors = errors.length > 0
$: hasFile = multi ? content?.length > 0 : !!content

async function onChange(evt = {}) {
  const input = evt.target
  const file = input.files[0]
  if (!file || loading) return

  loading = true

  try {
    const blob = await file.arrayBuffer()
    const contentType = file.type
    const fileName = file.name.substr(0, file.name.lastIndexOf("."))
    let fileNameAlreadyExists = false

    if (content) {
      if (multi) {
        fileNameAlreadyExists = content.find(
          (file) => file.split("/").pop() === fileName
        )
      } else {
        fileNameAlreadyExists = content === fileName
      }
    }

    if (fileNameAlreadyExists) {
      const errorMsg = errorUploadingFileWithSameNameText[$workflowLang]
      errors = [...errors, errorMsg]
      value = content
      return
    }

    const result = await API.post(`uploads/${wfId}`, {
      searchParams: { role },
      json: {
        field: name,
        fieldContentType: contentType,
        userId,
        fileName,
      },
    }).json()

    const { uploadUrl, url } = result ?? {}

    if (!uploadUrl || !url) {
      throw new Error("invalid upload URL response")
    }

    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: blob,
      headers: { "Content-Type": contentType },
    })

    if (!response.ok) {
      throw new Error("file upload failed")
    }

    const nextContent = multi ? [...content, url] : url

    content = nextContent
    value = multi ? [...nextContent] : nextContent
    evt.target.value = ""
  } catch (err) {
    let errorMsg = errorUploadingFileText[$workflowLang]
    errors = [...errors, errorMsg]
  } finally {
    if (input) input.value = ""
    loading = false
  }
}

async function remove(filePath) {
  try {
    loading = true

    await API.delete(`uploads/${wfId}`, {
      searchParams: { role: role },
      json: {
        field: name,
        userId,
        path: filePath,
      },
    })

    content = multi ? content.filter((file) => file !== filePath) : null
    value = multi ? [...content] : null
  } catch (err) {
    let errorMsg = errorRemovingFileText[$workflowLang]
    errors = [...errors, errorMsg]
  } finally {
    loading = false
  }
}
</script>

<div class="{$$props.className}" id="{name}">
  {#if label}
    <SubHeading className="mt-6 mb-8" content="{label}" />
  {/if}

  {#if description}
    <h3>{description}</h3>
  {/if}

  {#if hintTitle}
    <div><b>{hintTitle}</b></div>
  {/if}

  {#if hintItems}
    <div class="mb-8">
      <ul>
        {#each hintItems as item}
          <li><div class="hint">{item}</div></li>
        {/each}
      </ul>
    </div>
  {/if}

  {#if hasErrors}
    <div class="error">{errors[0]}</div>
  {/if}

  <input
    bind:value="{value}"
    bind:this="{uploadInput}"
    on:change="{onChange}"
    type="file"
    class="hidden"
    accept="{allowedExtension.join(',')}"
  />
  {#if !hasFile}
    {#if loading}
      <Button
        type="button"
        label="{loadingText[$workflowLang]}"
        hint="{buttonHint}"
        disabled="{loading}"
        outlined="{buttonOutlined}"
        filled="{buttonFilled}"
      />
    {:else}
      <Button
        type="button"
        label="{buttonLabel}"
        color="primary"
        hint="{buttonHint}"
        outlined="{true}"
        filled="{false}"
        on:click="{() => uploadInput.click()}"
      />
    {/if}
  {/if}

  {#if multi}
    {#each content as file}
      <div class="relative mt-8">
        <span class="remove" on:click="{() => remove(file)}">
          <i class="icon material-icons mr-2"> cancel </i>
          {removeText[$workflowLang]}
        </span>

        <p class="max-w-xs">{file.split("/").pop()}</p>
      </div>
    {/each}
  {:else if !multi && content}
    <div class="relative mt-8">
      <span class="remove" on:click="{() => remove(content)}">
        <i class="icon material-icons mr-2"> cancel </i>
        {removeText[$workflowLang]}
      </span>

      <p class="max-w-xs">{content.split("/").pop()}</p>
    </div>
  {/if}

  {#if multi && hasFile}
    {#if loading}
      <span class="addMore loading">
        <i class="icon material-icons mr-2"> hourglass_empty </i>
        {loadingText[$workflowLang]}
      </span>
    {:else}
      <span class="addMore" on:click="{() => uploadInput.click()}">
        <i class="icon material-icons mr-2"> add_circle </i>
        {addAnotherText[$workflowLang]}
      </span>
    {/if}
  {/if}
</div>
